import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '../../lib/api';
import {
  CancelOrderItemVars,
  CreateCustomerOrderInput,
  CreateOrderResponse,
  ListMyOrdersParams,
  MyOrdersResponse,
  UpdateMyOrderBody,
  UpdateMyOrderPayload,
} from './types';

export * from './types';

export function useCreateCustomerOrder() {
  const qc = useQueryClient();

  return useMutation<CreateOrderResponse, Error, CreateCustomerOrderInput>({
    mutationFn: async (payload) => {
      const { data } = await api.post('/checkout', payload);

      return data as CreateOrderResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });

      void qc.invalidateQueries({
        queryKey: ['org:orders:items'],
      });
    },
  });
}

export type MyOrderNote = {
  id: string;
  orderId: string;
  body: string;
  authorType: 'CUSTOMER' | 'ORG' | 'ADMIN';
  authorUserId: string | null;
  createdAt: string;
};

export type CustomerOrderDocument = {
  id: string;
  fileName: string;
  fileUrl: string;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedAt: string;
  canDelete: boolean;
  uploadedBy?: {
    firebaseUid: string;
    name: string | null;
    email: string | null;
  } | null;
};

export type CustomerOrderDocumentsResponse = {
  documents: CustomerOrderDocument[];
};

export function useListMyOrderNotes(orderId?: string) {
  return useQuery({
    queryKey: ['myOrderNotes', orderId],
    enabled: !!orderId,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } = await api.get(`/orders/${orderId}/notes`);

      return data as {
        notes: MyOrderNote[];
      };
    },
  });
}

export function useCreateMyOrderNote() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { orderId: string; body: string }) => {
      const { data } = await api.post(`/orders/${payload.orderId}/notes`, {
        body: payload.body,
      });

      return data as {
        note: MyOrderNote;
      };
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['myOrderNotes', vars.orderId],
      });

      void qc.invalidateQueries({
        queryKey: ['myOrders'],
      });
    },
  });
}

/**
 * Clears the customer's unread marker for an order's org notes.
 *
 * Invalidates the orders list so the red dot on "Updates and Notes"
 * disappears as soon as the notes have been opened.
 */
export function useMarkMyOrderNotesSeen() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const { data } = await api.post(`/orders/${orderId}/notes/mark-seen`);

      return data as {
        ok: boolean;
        markedCount: number;
      };
    },
    onSuccess: (result) => {
      if (result?.markedCount) {
        void qc.invalidateQueries({
          queryKey: ['me:orders'],
        });
      }
    },
  });
}

export function useCustomerOrderDetails(orderId?: string) {
  return useQuery({
    queryKey: ['customer:order:details', orderId],
    queryFn: async () => {
      const { data } = await api.get(`/orders/${orderId}`);

      return data;
    },
    enabled: Boolean(orderId),
  });
}

/* ───────────────────────────────────────────────────────────────────────────
 * The customer's own orders — merged here from the duplicate
 * `features/customerOrders` module (Phase 3).
 * ─────────────────────────────────────────────────────────────────────────── */

export type CustomerCreditSummaryResponse = {
  approvedCredit: number | null;
  availableCredit: number | null;
  overdueBalance: number | null;
  updatedAt: string | null;
  staleAt: string | null;
};

/** Customer-owned credit snapshot; the server resolves the signed-in customer. */
export function useCustomerCreditSummary() {
  return useQuery<CustomerCreditSummaryResponse>({
    queryKey: ['customer:credit-summary'],
    queryFn: async () => {
      const { data } = await api.get<CustomerCreditSummaryResponse>('/orders/credit-summary');
      return data;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

export function useListMyOrders(params: ListMyOrdersParams) {
  return useQuery({
    queryKey: ['me:orders', params],
    queryFn: async () =>
      (
        await api.get('/orders', {
          params,
        })
      ).data as MyOrdersResponse,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCancelMyOrderItem() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (vars: CancelOrderItemVars) => {
      const { orderId, itemId, reason } = vars;

      const { data } = await api.post(`/orders/${orderId}/items/${itemId}/cancel`, {
        reason: reason ?? null,
      });

      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['me:orders'],
      });

      void qc.invalidateQueries({
        queryKey: [
          'me:orders',
          {
            orderId: vars.orderId,
          },
        ],
      });
    },
  });
}

export function useUpdateMyOrder() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateMyOrderPayload) => {
      const { orderId, deliveryAddressId, poNumber } = payload;

      const data: UpdateMyOrderBody = {};

      if (deliveryAddressId) {
        data.deliveryAddressId = deliveryAddressId;
      }

      // `!== undefined` on purpose: null means "clear it".
      if (poNumber !== undefined) {
        data.poNumber = poNumber;
      }

      const response = await api.patch(`/orders/${orderId}`, data);

      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['me:orders'],
      });
    },
  });
}

export function useCustomerOrderDocuments(orderId?: string | null) {
  return useQuery<CustomerOrderDocumentsResponse>({
    queryKey: ['customer-order-documents', orderId],
    enabled: !!orderId,
    queryFn: async () => {
      const { data } = await api.get<CustomerOrderDocumentsResponse>(
        `/orders/${orderId}/documents`
      );

      return data;
    },
  });
}

export function useUploadCustomerOrderDocument(orderId?: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      if (!orderId) {
        throw new Error('Missing order id');
      }

      const formData = new FormData();

      formData.append('file', file);

      const { data } = await api.post<{
        document: CustomerOrderDocument;
      }>(`/orders/${orderId}/documents`, formData);

      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customer-order-documents', orderId],
      });
    },
  });
}

export function useDeleteCustomerOrderDocument(orderId?: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      if (!orderId) {
        throw new Error('Missing order id');
      }

      const { data } = await api.delete<{
        ok: true;
      }>(`/orders/${orderId}/documents/${documentId}`);

      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['customer-order-documents', orderId],
      });
    },
  });
}

export function useDownloadCustomerOrderDocument(orderId?: string | null) {
  return useMutation({
    mutationFn: async ({ documentId, fileName }: { documentId: string; fileName: string }) => {
      if (!orderId) {
        throw new Error('Missing order id');
      }

      const response = await api.get(`/orders/${orderId}/documents/${documentId}/download`, {
        responseType: 'blob',
      });

      const contentType = response.headers['content-type'] || 'application/octet-stream';

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const objectUrl = window.URL.createObjectURL(blob);

      const anchor = window.document.createElement('a');

      anchor.href = objectUrl;
      anchor.download = fileName || 'document';

      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.URL.revokeObjectURL(objectUrl);
    },
  });
}

export function usePreviewCustomerOrderDocument(orderId?: string | null) {
  return useMutation<Blob, Error, string>({
    mutationFn: async (documentId) => {
      if (!orderId) {
        throw new Error('Missing order id');
      }

      const response = await api.get<Blob>(
        `/orders/${encodeURIComponent(orderId)}/documents/${encodeURIComponent(documentId)}/download`,
        { responseType: 'blob' }
      );

      // A PDF MIME type is needed by iframe previews, even when storage
      // supplied the original PDF as application/octet-stream.
      return new Blob([response.data], { type: 'application/pdf' });
    },
  });
}

export interface CustomerOrderAuditLogEntry {
  id: string;
  createdAt: string;
  action: string;
  actorName: string | null;
  actorEmail: string | null;
  actorRole: string | null;
}

export interface CustomerOrderAuditLogResponse {
  logs: CustomerOrderAuditLogEntry[];
}

export function useCustomerOrderAuditLog(orderId?: string) {
  return useQuery<CustomerOrderAuditLogResponse>({
    queryKey: ['customer-orders', orderId, 'audit-log'],
    queryFn: async () => {
      const { data } = await api.get<CustomerOrderAuditLogResponse>(
        `/orders/${orderId}/audit-logs`
      );

      return data;
    },
    enabled: Boolean(orderId),
    staleTime: 60_000,
  });
}

/*
 * Add this to src/features/customer-orders/api.ts.
 *
 * This module is allowed to import/use the shared axios client; utilities are
 * intentionally kept transport-free by the project's lint architecture.
 */

export type CustomerOrderExportFilters = {
  status?: string;
  search?: string;
  productId?: string;
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc';
};

export type CustomerOrdersExportResponse = {
  page: number;
  pageSize: number;
  total: number;
  orders: any[];
};

const CUSTOMER_ORDER_EXPORT_PAGE_SIZE = 100;
const CUSTOMER_ORDER_EXPORT_BATCH_SIZE = 5;

function buildCustomerOrderExportParams(filters: CustomerOrderExportFilters, page: number) {
  return {
    page,
    pageSize: CUSTOMER_ORDER_EXPORT_PAGE_SIZE,
    status: filters.status || undefined,
    search: filters.search?.trim() || undefined,
    productId: filters.productId || undefined,
    sortBy: filters.sortBy || undefined,
    sortDir: filters.sortDirection || undefined,
  };
}

async function fetchCustomerOrderExportPage(
  filters: CustomerOrderExportFilters,
  page: number
): Promise<CustomerOrdersExportResponse> {
  const { data } = await api.get<CustomerOrdersExportResponse>('/orders', {
    params: buildCustomerOrderExportParams(filters, page),
  });

  return data;
}

/**
 * Fetch every customer order matching the current filters.
 *
 * The customer order endpoint caps pageSize at 100, so this follows all pages
 * in small batches instead of exporting only the currently visible table page.
 */
export async function fetchAllCustomerOrdersForExport(
  filters: CustomerOrderExportFilters
): Promise<any[]> {
  const firstPage = await fetchCustomerOrderExportPage(filters, 1);

  const totalPages = Math.max(
    1,
    Math.ceil((firstPage.total ?? 0) / CUSTOMER_ORDER_EXPORT_PAGE_SIZE)
  );

  const orders = [...(firstPage.orders ?? [])];

  for (let start = 2; start <= totalPages; start += CUSTOMER_ORDER_EXPORT_BATCH_SIZE) {
    const end = Math.min(totalPages, start + CUSTOMER_ORDER_EXPORT_BATCH_SIZE - 1);

    const pages = await Promise.all(
      Array.from({ length: end - start + 1 }, (_, index) =>
        fetchCustomerOrderExportPage(filters, start + index)
      )
    );

    pages.forEach((page) => {
      orders.push(...(page.orders ?? []));
    });
  }

  return orders;
}

export async function uploadCustomerOrderDocument(orderId: string, file: File) {
  const formData = new FormData();

  formData.append('file', file);

  const { data } = await api.post(`/orders/${orderId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return data;
}
